import { formatMoney } from "@/lib/money";
import type { StockHealthFields } from "../lib/stockHealth";
import StockStatus from "./StockStatus";

export interface HealthColumn {
  key: "value" | "incoming" | "doc" | "stockout" | "status";
  label: string;
  width: number;
}

// The stock health columns, between Total and the warehouse columns so they
// stay on screen without scrolling past every warehouse. Value only for users
// allowed to see costs.
export function healthColumns(valueVisible: boolean): HealthColumn[] {
  const cols: HealthColumn[] = [
    { key: "value", label: "Value", width: 96 },
    { key: "incoming", label: "Incoming", width: 84 },
    { key: "doc", label: "DOC", width: 96 },
    { key: "stockout", label: "Projected out of stock (days)", width: 112 },
    { key: "status", label: "Status", width: 156 },
  ];
  return valueVisible ? cols : cols.filter((c) => c.key !== "value");
}

function cellContent(key: HealthColumn["key"], row: StockHealthFields, currency: string) {
  switch (key) {
    case "value":
      return row.value == null ? null : (
        <span className="tabular-nums">{formatMoney(row.value, currency)}</span>
      );
    case "incoming":
      return row.incoming ? (
        <span className="flex flex-col items-end gap-0.5">
          <span className="tabular-nums">{row.incoming.toLocaleString()}</span>
          {row.incoming_undated > 0 && (
            <span className="text-xs tabular-nums" title="Order lines with no arrival date: left out of Projected out of stock.">
              {row.incoming_undated.toLocaleString()} no date
            </span>
          )}
        </span>
      ) : (
        <span style={{ color: "var(--chart-muted)" }}>–</span>
      );
    case "doc":
      return row.doc == null ? (
        <span style={{ color: "var(--chart-muted)" }}>–</span>
      ) : (
        <span className="tabular-nums">{row.doc.toLocaleString()}</span>
      );
    case "stockout":
      return row.stockout_days == null ? null : (
        <span className="tabular-nums">
          {row.stockout_after_plan && "> "}
          {row.stockout_days.toLocaleString()}
        </span>
      );
    case "status":
      return <StockStatus row={row} className="flex flex-col items-end gap-0.5" />;
  }
}

interface Props {
  columns: HealthColumn[];
  row: StockHealthFields;
  currency: string;
  onClick: () => void;
}

export default function StockHealthCells({ columns, row, currency, onClick }: Props) {
  return (
    <>
      {columns.map((c, i) => (
        <div
          key={c.key}
          onClick={onClick}
          className="cursor-pointer"
          style={{
            width: c.width,
            flexShrink: 0,
            borderRight:
              i === columns.length - 1 ? "2px solid var(--border)" : "1px solid var(--border)",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            textAlign: "right",
            padding: "0 8px",
            fontSize: 12,
            lineHeight: 1.2,
          }}
        >
          {cellContent(c.key, row, currency)}
        </div>
      ))}
    </>
  );
}
